import { useState } from "react";
import { useRouter } from "next/navigation";
import { getBrowserSupabaseClient } from "@wtc/supabase";
import type { AuthFormActions, AuthFormState } from "./use-auth-forms/types";
import { createSignInHandler } from "./use-auth-forms/handle-sign-in";
import { createSignUpHandler } from "./use-auth-forms/handle-sign-up";
import { createMagicLinkHandler } from "./use-auth-forms/handle-magic-link";
import { createGoogleSignInHandler } from "./use-auth-forms/handle-google-sign-in";
import { createAppleSignInHandler } from "./use-auth-forms/handle-apple-sign-in";
import { createPasskeySignInHandler } from "./use-auth-forms/handle-passkey-sign-in";

export type { AuthFormState, AuthFormActions } from "./use-auth-forms/types";

export function useAuthForms(redirectAfterLogin?: string | null): AuthFormState & AuthFormActions {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const router = useRouter();
  const supabase = getBrowserSupabaseClient();

  const handleSignIn = createSignInHandler({
    supabase,
    router,
    email,
    password,
    redirectAfterLogin,
    setIsLoading,
    setError,
    setSuccess,
  });

  const handleSignUp = createSignUpHandler({
    supabase,
    router,
    email,
    password,
    firstName,
    lastName,
    setEmail,
    setPassword,
    setFirstName,
    setLastName,
    setIsSignUp,
    setIsLoading,
    setError,
    setSuccess,
  });

  const handleMagicLink = createMagicLinkHandler({
    supabase,
    email,
    setIsLoading,
    setError,
    setSuccess,
  });

  const handleGoogleSignIn = createGoogleSignInHandler({
    supabase,
    setIsLoading,
    setError,
    setSuccess,
  });

  const handleAppleSignIn = createAppleSignInHandler({
    supabase,
    setIsLoading,
    setError,
    setSuccess,
  });

  const handlePasskeySignIn = createPasskeySignInHandler({
    supabase,
    router,
    redirectAfterLogin,
    setIsLoading,
    setError,
    setSuccess,
  });

  return {
    // State
    email,
    password,
    firstName,
    lastName,
    isSignUp,
    isLoading,
    error,
    success,

    // Actions
    setEmail,
    setPassword,
    setFirstName,
    setLastName,
    setIsSignUp,
    setIsLoading,
    setError,
    setSuccess,
    handleSignIn,
    handleSignUp,
    handleMagicLink,
    handleGoogleSignIn,
    handleAppleSignIn,
    handlePasskeySignIn,
  };
}
